import {
  darken as polishedDarken,
  desaturate as polishedDesaturate,
  invert as polishedInvert,
  lighten as polishedLighten,
  mix as polishedMix,
  opacify as polishedOpacify,
  rgba as polishedRgba,
  saturate as polishedSaturate,
  setLightness as polishedSetLightness,
  shade as polishedShade,
  tint as polishedTint,
  transparentize as polishedTransparentize,
} from 'polished';

/**
 * Drop-in, `var()`-aware replacement for the `polished` color helpers.
 *
 * `polished` runs in JS and can only transform colors it can parse (hex, rgb,
 * hsl, named). It throws on a CSS custom property such as `var(--primary)`
 * because that value is only resolved by the browser at paint time.
 *
 * Each helper here keeps the exact `polished` signature and:
 * - delegates to `polished` for any parseable color → byte-for-byte identical
 *   output, so existing call sites that pass hex/rgb values are unchanged;
 * - emits a CSS expression (`hsl(from …)` relative color, or `color-mix()`)
 *   when the color is a dynamic CSS expression (`var()`, `color-mix()`, a
 *   relative color), so theme values backed by CSS variables keep working.
 *
 * Relative colors (`hsl(from …)`) mirror polished's HSL model for lightness /
 * saturation; `color-mix()` covers alpha and blending.
 */

const DYNAMIC_COLOR_PATTERN = /var\(|color-mix\(|\b(?:rgb|rgba|hsl|hsla|oklch|oklab|lab|lch)\(\s*from\b/i;

const isDynamicColor = (color: unknown): color is string =>
  typeof color === 'string' && DYNAMIC_COLOR_PATTERN.test(color);

const toPercentage = (amount: number): string => `${amount * 100}%`;

export const darken = (amount: number, color: string): string => {
  if (isDynamicColor(color)) {
    return `hsl(from ${color} h s calc(l - ${toPercentage(amount)}))`;
  }

  return polishedDarken(amount, color);
};

export const lighten = (amount: number, color: string): string => {
  if (isDynamicColor(color)) {
    return `hsl(from ${color} h s calc(l + ${toPercentage(amount)}))`;
  }

  return polishedLighten(amount, color);
};

export const desaturate = (amount: number, color: string): string => {
  if (isDynamicColor(color)) {
    return `hsl(from ${color} h calc(s - ${toPercentage(amount)}) l)`;
  }

  return polishedDesaturate(amount, color);
};

export const saturate = (amount: number, color: string): string => {
  if (isDynamicColor(color)) {
    return `hsl(from ${color} h calc(s + ${toPercentage(amount)}) l)`;
  }

  return polishedSaturate(amount, color);
};

export const setLightness = (lightness: number, color: string): string => {
  if (isDynamicColor(color)) {
    return `hsl(from ${color} h s ${toPercentage(lightness)})`;
  }

  return polishedSetLightness(lightness, color);
};

export const invert = (color: string): string => {
  if (isDynamicColor(color)) {
    return `rgb(from ${color} calc(255 - r) calc(255 - g) calc(255 - b))`;
  }

  return polishedInvert(color);
};

export const transparentize = (amount: number, color: string): string => {
  if (isDynamicColor(color)) {
    return `color-mix(in srgb, ${color} ${toPercentage(1 - amount)}, transparent)`;
  }

  return polishedTransparentize(amount, color);
};

export const opacify = (amount: number, color: string): string => {
  if (isDynamicColor(color)) {
    return color;
  }

  return polishedOpacify(amount, color);
};

export const tint = (amount: number, color: string): string => {
  if (isDynamicColor(color)) {
    return `color-mix(in srgb, white ${toPercentage(amount)}, ${color})`;
  }

  return polishedTint(amount, color);
};

export const shade = (amount: number, color: string): string => {
  if (isDynamicColor(color)) {
    return `color-mix(in srgb, black ${toPercentage(amount)}, ${color})`;
  }

  return polishedShade(amount, color);
};

export const mix = (weight: number, color: string, otherColor: string): string => {
  if (isDynamicColor(color) || isDynamicColor(otherColor)) {
    return `color-mix(in srgb, ${color} ${toPercentage(weight)}, ${otherColor})`;
  }

  return polishedMix(weight, color, otherColor);
};

type RgbaArgs =
  | [color: string, alpha: number]
  | [value: { red: number; green: number; blue: number; alpha?: number }]
  | [red: number, green: number, blue: number, alpha: number];

export const rgba = (...args: RgbaArgs): string => {
  const [color, alpha] = args;

  if (args.length === 2 && isDynamicColor(color)) {
    return `color-mix(in srgb, ${color} ${toPercentage(alpha as number)}, transparent)`;
  }

  // @ts-expect-error — forward the original overloaded signature to polished.
  return polishedRgba(...args);
};
